import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Trans, useTranslation } from "react-i18next";
import validator from "validator";
import BusinessActions from "../Redux/actions/business-actions";
import "../Styles/components/authorizedSignatoryDialog.scss";

function SignatureIcon() {
	return (
		<svg
			className="signatory_dialog__icon"
			width="18"
			height="14"
			viewBox="0 0 18 14"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
		>
			<path
				d="M1 10.5C2.5 6.5 4.2 3.2 6.1 3.5C8.2 3.8 8.5 9.2 10.2 9.5C12 9.8 13.2 5.2 14.8 4.8C15.8 4.55 16.6 6.2 17 7.5"
				stroke="currentColor"
				strokeWidth="1.6"
				strokeLinecap="round"
			/>
		</svg>
	);
}

/**
 * Shown before PSP Activate when Custom PSP is enabled.
 * Confirm existing Authorised Signatory or update via business PUT;
 * backend sends the new agreement on configure/activate.
 */
export default function AuthorizedSignatoryDialog({
	visible,
	onHide,
	onConfirm,
	loading = false,
}) {
	const { t } = useTranslation("common");
	const dispatch = useDispatch();
	const business = useSelector((state) => state.businesses.business);
	const updating = useSelector((state) => state.businesses.updating);
	const errorUpdating = useSelector((state) => state.businesses.errorUpdating);

	const [isEditing, setIsEditing] = useState(false);
	const [pendingConfirm, setPendingConfirm] = useState(false);
	const [form, setForm] = useState({
		signerFullName: "",
		signerCapacity: "",
		signerEmail: "",
	});
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (visible && business) {
			setIsEditing(false);
			setPendingConfirm(false);
			setErrors({});
			setForm({
				signerFullName: business.signerFullName || "",
				signerCapacity: business.signerCapacity || "",
				signerEmail: business.signerEmail || "",
			});
		}
	}, [visible, business]);

	useEffect(() => {
		if (pendingConfirm && !updating) {
			setPendingConfirm(false);
			if (!errorUpdating) {
				window.dispatchEvent(new Event("reveknew:clear-global-search"));
				onConfirm?.();
			}
		}
	}, [pendingConfirm, updating, errorUpdating, onConfirm]);

	const handleHide = () => {
		window.dispatchEvent(new Event("reveknew:clear-global-search"));
		onHide?.();
	};

	const validate = () => {
		const next = {};
		if (!form.signerFullName?.trim()) {
			next.signerFullName = t("business.error_rep_name_required");
		}
		if (!form.signerCapacity?.trim()) {
			next.signerCapacity = t("business.error_rep_role_required");
		}
		if (!form.signerEmail?.trim()) {
			next.signerEmail = t("business.error_signer_email_required");
		} else if (!validator.isEmail(form.signerEmail.trim())) {
			next.signerEmail = t("business.error_signer_email_invalid");
		}
		setErrors(next);
		return Object.keys(next).length === 0;
	};

	const handleConfirm = () => {
		if (!business?.id) return;
		if (isEditing) {
			if (!validate()) return;
			const payload = {
				...business,
				signerFullName: form.signerFullName.trim(),
				signerCapacity: form.signerCapacity.trim(),
				signerEmail: form.signerEmail.trim(),
			};
			setPendingConfirm(true);
			dispatch(BusinessActions.businessUpdateRequest(business.id, payload));
		} else {
			window.dispatchEvent(new Event("reveknew:clear-global-search"));
			onConfirm?.();
		}
	};

	const busy = loading || updating;

	const footer = (
		<div className="signatory_dialog__actions">
			<Button
				label={t("common.cancel")}
				className="signatory_dialog__btn-cancel"
				onClick={handleHide}
				disabled={busy}
			/>
			{!isEditing && (
				<Button
					label={t("idoc.signatory_update")}
					className="signatory_dialog__btn-update"
					onClick={() => setIsEditing(true)}
					disabled={busy}
				/>
			)}
			<Button
				label={isEditing ? t("idoc.signatory_save_and_activate") : t("idoc.signatory_confirm")}
				className="signatory_dialog__btn-confirm"
				loading={busy}
				onClick={handleConfirm}
				autoFocus
			/>
		</div>
	);

	return (
		<Dialog
			header={t("idoc.signatory_dialog_header")}
			visible={visible}
			style={{ width: "min(480px, 90vw)" }}
			onHide={handleHide}
			footer={footer}
			className="signatory_dialog tier_dialog"
		>
			<p className="signatory_dialog__intro">
				<Trans
					i18nKey="idoc.signatory_dialog_intro"
					components={{ 1: <strong /> }}
				/>
			</p>

			<div className="signatory_dialog__card">
				<div className="signatory_dialog__card-title">
					<SignatureIcon />
					<span>{t("idoc.signatory_dialog_card_title")}</span>
				</div>

				{!isEditing ? (
					<>
						<p className="signatory_dialog__field">
							<span className="signatory_dialog__label">{t("business.rep_name_label")}: </span>
							<span className="signatory_dialog__value">{business?.signerFullName || "—"}</span>
						</p>
						<p className="signatory_dialog__field">
							<span className="signatory_dialog__label">{t("business.rep_role_label")}: </span>
							<span className="signatory_dialog__value">{business?.signerCapacity || "—"}</span>
						</p>
						<p className="signatory_dialog__field">
							<span className="signatory_dialog__label">{t("idoc.signatory_email_label")}: </span>
							<span className="signatory_dialog__value">{business?.signerEmail || "—"}</span>
						</p>
					</>
				) : (
					<div className="signatory_dialog__form">
						<div>
							<label htmlFor="auth_signer_name" className="form-label">
								{errors.signerFullName || t("business.rep_name_label")}
							</label>
							<InputText
								id="auth_signer_name"
								name="auth_signer_name"
								autoComplete="off"
								value={form.signerFullName}
								className={errors.signerFullName ? "p-invalid" : undefined}
								onChange={(e) => setForm((prev) => ({ ...prev, signerFullName: e.target.value }))}
							/>
						</div>
						<div>
							<label htmlFor="auth_signer_capacity" className="form-label">
								{errors.signerCapacity || t("business.rep_role_label")}
							</label>
							<InputText
								id="auth_signer_capacity"
								name="auth_signer_capacity"
								autoComplete="off"
								value={form.signerCapacity}
								className={errors.signerCapacity ? "p-invalid" : undefined}
								onChange={(e) => setForm((prev) => ({ ...prev, signerCapacity: e.target.value }))}
							/>
						</div>
						<div>
							<label htmlFor="auth_signer_email" className="form-label">
								{errors.signerEmail || t("idoc.signatory_email_label")}
							</label>
							<InputText
								id="auth_signer_email"
								name="auth_signer_email"
								autoComplete="off"
								value={form.signerEmail}
								className={errors.signerEmail ? "p-invalid" : undefined}
								onChange={(e) => setForm((prev) => ({ ...prev, signerEmail: e.target.value }))}
							/>
						</div>
					</div>
				)}
			</div>

			{!isEditing && (
				<p className="signatory_dialog__note">{t("idoc.signatory_dialog_note")}</p>
			)}
		</Dialog>
	);
}
